import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FileText, TrendingUp, TrendingDown, DollarSign, Hash } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';

interface DailyReport {
  id: string;
  agentId: string;
  reportDate: string;
  cashInCount: number;
  cashInVolume: number;
  cashOutCount: number;
  cashOutVolume: number;
  commissionsTotal: number;
  floatStart: number;
  floatEnd: number;
  status?: string;
}

interface DailyReportCardProps {
  report: DailyReport;
  onClick?: () => void;
}

export const DailyReportCard = ({ report, onClick }: DailyReportCardProps) => {
  const netFlow = (report.cashInVolume || 0) - (report.cashOutVolume || 0);
  const isPositive = netFlow >= 0;
  const transactionCount = (report.cashInCount || 0) + (report.cashOutCount || 0);

  // Validar que la fecha sea válida
  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return 'Fecha no disponible';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      return format(date, "d 'de' MMMM, yyyy", { locale: es });
    } catch (error) {
      // date formatting error handled by fallback return
      return 'Fecha inválida';
    }
  };

  return (
    <Card
      variant="bordered"
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="text-primary-600" size={20} />
            <CardTitle className="text-base">
              {formatDate(report.reportDate)}
            </CardTitle>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          {/* Cash In */}
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-success-600" size={16} />
            </div>
            <div>
              <p className="text-xs text-gray-600">Cash In</p>
              <p className="text-sm font-semibold text-gray-900">
                ${(report.cashInVolume || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Cash Out */}
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="text-primary-600" size={16} />
            </div>
            <div>
              <p className="text-xs text-gray-600">Cash Out</p>
              <p className="text-sm font-semibold text-gray-900">
                ${(report.cashOutVolume || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Commissions */}
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-warning-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-warning-600" size={16} />
            </div>
            <div>
              <p className="text-xs text-gray-600">Comisiones</p>
              <p className="text-sm font-semibold text-warning-600">
                ${(report.commissionsTotal || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Transactions */}
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <Hash className="text-gray-600" size={16} />
            </div>
            <div>
              <p className="text-xs text-gray-600">Transacciones</p>
              <p className="text-sm font-semibold text-gray-900">{transactionCount}</p>
            </div>
          </div>
        </div>

        {/* Net Flow */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Flujo neto:</span>
            <span
              className={`text-base font-bold ${
                isPositive ? 'text-success-600' : 'text-danger-600'
              }`}
            >
              {isPositive ? '+' : ''}${netFlow.toLocaleString()}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
